import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { WarningOctagon, Trash } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

/**
 * Danger-zone dialog: lets a signed-in NON-admin user permanently delete their
 * own account (including cascade of courses/bookings/reviews). Requires the user
 * to re-enter their password and type "DELETE" to confirm.
 */
export default function DeleteAccountDialog({ triggerClassName = "" }) {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmWord, setConfirmWord] = useState("");
  const [loading, setLoading] = useState(false);

  const CONFIRM_TOKEN = t("delete_account.confirm_token"); // e.g. ELIMINA / LÖSCHEN / SUPPRIMER

  const reset = () => { setPassword(""); setConfirmWord(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (confirmWord.trim().toUpperCase() !== CONFIRM_TOKEN.toUpperCase()) {
      toast.error(t("delete_account.confirm_mismatch", { token: CONFIRM_TOKEN }));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/delete-account", { password });
      toast.success(t("delete_account.success"));
      // Ensure auth state clears, then redirect home
      await logout?.();
      setOpen(false);
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={`border-red-500/40 text-red-400 hover:bg-red-900/20 hover:border-red-500 hover:text-red-300 uppercase tracking-widest text-xs font-bold rounded-none ${triggerClassName}`}
          data-testid="delete-account-trigger"
        >
          <Trash size={14} className="mr-2" weight="bold" />
          {t("delete_account.trigger_label")}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border border-red-500/30 text-white rounded-none max-w-md" data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase text-2xl font-black flex items-center gap-2 text-red-400">
            <WarningOctagon size={22} weight="fill" />
            {t("delete_account.title")}
          </DialogTitle>
          <DialogDescription className="text-zinc-300 pt-2 leading-relaxed">
            {t("delete_account.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold" htmlFor="del-pwd">
              {t("delete_account.password_label")}
            </Label>
            <Input
              id="del-pwd"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="bg-black border-white/10 rounded-none text-white mt-1"
              data-testid="delete-account-password"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold" htmlFor="del-token">
              {t("delete_account.confirm_label", { token: CONFIRM_TOKEN })}
            </Label>
            <Input
              id="del-token"
              type="text"
              value={confirmWord}
              onChange={(e) => setConfirmWord(e.target.value)}
              required
              placeholder={CONFIRM_TOKEN}
              className="bg-black border-white/10 rounded-none text-white mt-1 font-mono uppercase"
              data-testid="delete-account-confirm-token"
            />
          </div>
          <DialogFooter className="gap-2 mt-4">
            <Button
              type="button"
              onClick={() => setOpen(false)}
              variant="ghost"
              className="text-zinc-300 hover:bg-white/5 uppercase tracking-widest text-xs font-bold rounded-none"
              data-testid="delete-account-cancel"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-red-600 hover:bg-red-500 text-white uppercase tracking-[0.2em] font-bold rounded-none h-11 px-6"
              data-testid="delete-account-submit"
            >
              {loading ? t("common.loading") : t("delete_account.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
